import React from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';

import { dateTimeFormat } from '../../../../util/GlobalConstant';
import GC from '../../../../util/GlobalConstant';

import {
  ResponsiveContainer,
  AreaChart,
  XAxis,
  YAxis,
  Area,
  CartesianGrid,
  Tooltip,
  Label,
  Legend
} from 'recharts';

import { flattenChartData } from './chartsUtil';

import style from './index.scss';

/**
 * NoDataPanel
 * @param onTogglePanelVisibility
 * @param onReload
 * @returns {*}
 * @constructor
 */
const NoDataPanel = ({ onTogglePanelVisibility, onReload }) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="row">
          <div className="col-md-6">
            <p>No Issue & L1</p>
          </div>

          <div className="col-md-6">
            <h5 className="text-right">
              <i
                onClick={onTogglePanelVisibility}
                className="fa fa-window-minimize"></i>
            </h5>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="row">
          <div className="col-md-12">
            <p>No data exists for the period selected.</p>
            <p>
              <a className={style.retryLink} onClick={onReload}>
                Click here to fetch data again.
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * IsLoadingPanel
 * @param onTogglePanelVisibility
 * @returns {*}
 * @constructor
 */
const IsLoadingPanel = ({ onTogglePanelVisibility }) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="row">
          <div className="col-md-6">
            <p>No Issue & L1</p>
          </div>

          <div className="col-md-6">
            <h5 className="text-right">
              <i
                onClick={onTogglePanelVisibility}
                className="fa fa-window-minimize"></i>
            </h5>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="row">
          <div className="col-md-12">
            <p>Loading chart data...</p>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * MinimizedPanel
 * @param onTogglePanelVisibility
 * @returns {*}
 * @constructor
 */
const MinimizedPanel = ({ onTogglePanelVisibility }) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="row">
          <div className="col-md-6">
            <p>No Issue & L1</p>
          </div>

          <div className="col-md-6">
            <h5 className="text-right">
              <i
                onClick={onTogglePanelVisibility}
                className="fa fa-window-maximize"></i>
            </h5>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * NoIssueL1Chart
 * @param props
 * @returns {*}
 * @constructor
 */
export const NoIssueL1Chart = (props) => {
  const {
    data,
    isLoading,
    isCollapsed,
    onTogglePanelVisibility,
    onReload
  } = props;

  if (isCollapsed) {
    return <MinimizedPanel onTogglePanelVisibility={onTogglePanelVisibility} />;
  }

  if (isLoading) {
    return <IsLoadingPanel onTogglePanelVisibility={onTogglePanelVisibility} />;
  }

  if (Array.isArray(data) && data.length > 0) {
    let formattedData = [];
    data.forEach((item) => {
      const formattedItem = {
        ...item,
        date: moment
          .utc(item.date, [dateTimeFormat])
          .local()
          .format('DD/MM/YYYY')
      };

      formattedData.push(formattedItem);
    });

    const flattenedData = flattenChartData(formattedData, [
      'alerts',
      'noIssue',
      'l1'
    ]);

    return (
      <div className="card">
        <div className="card-header">
          <div className="row">
            <div className="col-md-6">
              <p>No Issue & L1</p>
            </div>

            <div className="col-md-6">
              <h5 className="text-right">
                <i
                  onClick={onTogglePanelVisibility}
                  className="fa fa-window-minimize"></i>
              </h5>
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="row">
            <div className="col-md-12">
              {/* Figure Area Chart goes here */}
              <ResponsiveContainer
                width="100%"
                height={260}
                margin={{ top: 20, right: 30, left: 20, bottom: 10 }}>
                <AreaChart data={flattenedData}>
                  <defs>
                    <linearGradient id="colorL1" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={GC.alertColor}
                        stopOpacity={0.5}
                      />
                      <stop
                        offset="95%"
                        stopColor={GC.alertColor}
                        stopOpacity={0}
                      />
                    </linearGradient>
                    <linearGradient id="colorAlert" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={GC.autoCloseColor}
                        stopOpacity={0.5}
                      />
                      <stop
                        offset="95%"
                        stopColor={GC.autoCloseColor}
                        stopOpacity={0}
                      />
                    </linearGradient>
                    <linearGradient
                      id="colorNoIssue"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1">
                      <stop
                        offset="5%"
                        stopColor={GC.noIssueColor}
                        stopOpacity={0.5}
                      />
                      <stop
                        offset="95%"
                        stopColor={GC.noIssueColor}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <XAxis
                    height={60}
                    tickCount={5}
                    dataKey="date"
                    tickFormatter={dateTickFormatter}
                    tick={<CustomizedAxisTick />}
                  />
                  <YAxis />

                  <CartesianGrid strokeDasharray="5 5" vertical={false} />
                  <Tooltip content={<CustomTooltip data={flattenedData} />} />
                  <Legend formatter={legendFormatter} />
                  <Area
                    type="monotone"
                    dataKey="alerts"
                    stroke={GC.autoCloseColor}
                    fillOpacity={0.8}
                    fill="url(#colorAlert)"
                  />
                  <Area
                    type="monotone"
                    dataKey="noIssue"
                    stroke={GC.noIssueColor}
                    fillOpacity={0.8}
                    fill="url(#colorNoIssue)"
                  />
                  <Area
                    type="monotone"
                    dataKey="l1"
                    stroke={GC.alertColor}
                    fillOpacity={0.8}
                    fill="url(#colorL1)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <NoDataPanel
      onReload={onReload}
      onTogglePanelVisibility={onTogglePanelVisibility}
    />
  );
};
/**
 * dateTickFormatter
 * @param tickitem
 * @returns {string}
 */
const dateTickFormatter = (tickitem) => {
  const tickDate = moment(tickitem, [dateTimeFormat]);
  if (tickDate.isValid()) {
    return moment(tickitem, [dateTimeFormat]).format('DD/MM/YYYY');
  }

  return '';
};
/**
 * CustomTooltip
 * @param props
 * @returns {null|*}
 * @constructor
 */
const CustomTooltip = (props) => {
  const { active, payload, text, label, data } = props;

  if (!data || !payload) {
    return null;
  }

  let pointLists = [];
  payload.forEach((item, i) => {
    let itemNameLabel = item.name;
    if (item.name === 'l1') {
      itemNameLabel = 'L1';
    }

    if (item.name === 'noIssue') {
      itemNameLabel = 'No Issue';
    }

    if (item.name === 'alerts') {
      itemNameLabel = 'Alerts';
    }
    pointLists.push(
      <li key={i} style={{ color: `${item.stroke}` }}>
        {itemNameLabel} : <span style={{ float: 'right' }}>{item.value}</span>
      </li>
    );
  });

  let accumulatedLists = [];
  payload.forEach((item, i) => {
    let itemNameLabel = item.name;
    if (item.name === 'l1') {
      itemNameLabel = 'L1';
    }

    if (item.name === 'noIssue') {
      itemNameLabel = 'No Issue';
    }

    if (item.name === 'alerts') {
      itemNameLabel = 'Alerts';
    }
    accumulatedLists.push(
      <li key={i} style={{ color: `${item.stroke}` }}>
        {itemNameLabel} :{' '}
        <span style={{ float: 'right' }}>
          {accumulater(data, label, item.name)}
        </span>
      </li>
    );
  });

  let startingDate = '';
  let selectedDate = '';

  const startingDateValue = moment(data[0].date, ['DD/MM/YYYY']);
  if (startingDateValue.isValid()) {
    startingDate = data[0].date;
  }

  const selectedDateValue = moment(label, ['DD/MM/YYYY']);
  if (selectedDateValue.isValid()) {
    selectedDate = label;
  }

  return active ? (
    <div className={style.customTooltip}>
      <div className="row">
        <div className="col-sm-12">
          <p>
            <i className="fa fa-calendar" /> {selectedDate}
          </p>
          <ul className="list-unstyled">{pointLists}</ul>
        </div>

        <svg height="10" width="180">
          <line
            x1="20"
            y1="2"
            x2="180"
            y2="2"
            style={{ stroke: 'rgb(133,133,0)', strokeWidth: 1 }}
          />
        </svg>

        <div className="col-sm-12">
          <p>
            <i className="fa fa-calendar" /> {startingDate} -> {selectedDate}
          </p>
          <ul className="list-unstyled">{accumulatedLists}</ul>
        </div>
      </div>
    </div>
  ) : null;
};
/**
 * accumulater
 * @param data
 * @param label
 * @param name
 * @returns {number}
 */
const accumulater = (data, label, name) => {
  let result = 0;

  let i = 0;
  let isLooping = true;
  while (isLooping) {
    const item = data[i];
    result += item[name];

    i++;

    if (item.date === label) {
      isLooping = false;
    }
  }

  return result;
};
/**
 * legendFormatter
 * @param value
 * @param entry
 * @param index
 * @returns {string}
 */
const legendFormatter = (value, entry, index) => {
  if (value) {
    if (value === 'l1') {
      return 'L1';
    }

    if (value === 'noIssue') {
      return 'No Issue';
    }

    if (value === 'alerts') {
      return 'Alerts';
    }
  }

  return '';
};
/**
 * CustomizedAxisTick
 * @param props
 * @returns {*}
 * @constructor
 */
const CustomizedAxisTick = (props) => {
  const { x, y, stroke, payload, visibleTicksCount, index } = props;
  const tickValue = moment(payload.value);

  if (tickValue.isValid()) {
    const formattedTickDate = tickValue.local().format('DD/MM/YYYY');
    if (index === visibleTicksCount - 1) {
      return (
        <g transform={`translate(${x + 20},${y})`}>
          <text
            x={0}
            y={0}
            dy={12}
            textAnchor="end"
            fill="#666"
            transform="rotate(-35)">
            {payload.value}
          </text>
        </g>
      );
    } else {
      return (
        <g transform={`translate(${x},${y})`}>
          <text
            x={0}
            y={0}
            dy={12}
            textAnchor="end"
            fill="#666"
            transform="rotate(-35)">
            {payload.value}
          </text>
        </g>
      );
    }
  }

  if (index === visibleTicksCount - 1) {
    return (
      <g transform={`translate(${x + 20},${y})`}>
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="end"
          fill="#666"
          transform="rotate(-35)">
          {payload.value}
        </text>
      </g>
    );
  } else {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="end"
          fill="#666"
          transform="rotate(-35)">
          {payload.value}
        </text>
      </g>
    );
  }
};

NoIssueL1Chart.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  onTogglePanelVisibility: PropTypes.func,
  onReload: PropTypes.func
};
